import React, { useMemo } from 'react'
import styled from 'particles/themes/styled'
import { GridAlignment, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { HighlightCell } from 'components/Shared/MuiTable/Cell/HighlightCell'
import { PLATFORM_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { CampaignState } from 'state/campaigns'
import { PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { GridColDefProps } from 'components/Submissions/Review/Submissions/SimpleTable/constants'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { useNewTranslation } from 'hooks'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { useSelector } from 'react-redux'
import { EngagementId, selectTasks } from 'state/tasks'
import _ from 'lodash'
import { getStepDataHeaderCell } from 'components/Shared/MuiTable/Cell/StepDataHeaderCell'
import i18n from 'config/i18n'
import Text from 'particles/Text'
import Icon, { IconColor } from 'particles/Icon'
import { UpdateAmbassadorFavoritePayload } from 'state/campaignReport'
import { GetIndividualPerformanceResponse, GetIndividualPerformanceStatsResponse } from 'services/api/campaignReport'
import { generateUuid } from 'services/uuid'
import { getEngagementTranslation } from 'components/utils/translationsMap'
import { usePermission } from 'hooks/permissions'
import { Ambassador } from 'components/Shared/Permission/enums'
import { convertToThousandSeparator } from 'components/utils'

export type SubmissionStatus = 'in_progress' | 'completed' | 'drop_out' | 'rejected'

export interface IndividualPerformanceRowProps {
    id: string
    uuid: string
    favorite: boolean
    status: {
        value: string
        color: IconColor
    }
    profile: {
        profile: PartiposterProfile
    }
    socialMediaInfo: {
        profiles: (SocialMediaAccount | undefined)[]
    }
    instagramFollowerCount: {
        value: string
    }
    facebookFollowerCount: {
        value: string
    }
    tiktokFollowerCount: {
        value: string
    }
    twitterFollowerCount: {
        value: string
    }
    instagramUrl: string
    twitterUrl: string
    tiktokUrl: string
    facebookUrl: string
}

interface AggregateRowProps {
    id: string
    uuid: string
    total: {
        value: number
    }
    socialMediaInfo: {
        profiles: {
            platform: number
            follower_count: number
        }[]
    }
    instagramFollowerCount: {
        value: string
    }
    facebookFollowerCount: {
        value: string
    }
    tiktokFollowerCount: {
        value: string
    }
    twitterFollowerCount: {
        value: string
    }
}

interface RowProps {
    rows: IndividualPerformanceRowProps[]
    aggregateRow: AggregateRowProps
}

const SUBMISSION_STATUS: Record<SubmissionStatus, { value: string; color: IconColor }> = {
    in_progress: {
        value: i18n.t('statusLabel.inProgress'),
        color: 'warning',
    },
    completed: {
        value: i18n.t('statusLabel.completed'),
        color: 'positive',
    },
    drop_out: {
        value: i18n.t('tag.dropOut'),
        color: 'error',
    },
    rejected: {
        value: i18n.t('statusLabel.rejected'),
        color: 'error',
    },
}

const aggregateRowId = generateUuid()

const getPerformanceColumns = (individualPerformanceData: GetIndividualPerformanceResponse): GridColDefProps[] => {
    const [t] = useNewTranslation()
    // Get all tasks from all results
    const allTasks = individualPerformanceData.results.flatMap(result => result.tasks)

    // Create a Set to track unique task title + metric combinations
    const uniqueColumns = new Set()
    const columns: GridColDefProps[] = []

    allTasks.forEach(task => {
        if (task.performance) {
            Object.keys(task.performance).forEach(metric => {
                const columnKey = `${task.uuid}__${metric}`
                if (!uniqueColumns.has(columnKey)) {
                    uniqueColumns.add(columnKey)
                    columns.push({
                        field: `${task.uuid}__${metric}`,
                        headerName: `${task.title} - ${getEngagementTranslation[metric]?.(t)}`,
                        headerClassName: 'performance-header',
                        width: 120,
                        resizable: true,
                        renderHeader: getStepDataHeaderCell(' - '),
                        renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                        align: 'right',
                        sortable: false,
                        filterable: false,
                    })
                }
            })
        }
    })

    return columns
}

const isRateMetric = (metric: string) => {
    return metric === EngagementId.CONTENT_ENGAGEMENT_RATE || metric === EngagementId.ENGAGEMENT_RATE
}

const transformPerformanceStats = (data: GetIndividualPerformanceStatsResponse, totalCount: number) => {
    // Transform follower counts into profiles array
    const profiles = Object.entries(data.profile_follower).map(([key, count]) => ({
        platform: Number(key),
        follower_count: count,
    }))

    // Transform tasks performance into separate fields
    const taskPerformance = data.tasks.reduce((acc, task) => {
        const metrics: Record<string, { value: string }> = {}
        Object.entries(task.performance).forEach(([metric, value]) => {
            metrics[`${task.uuid}__${metric}`] = {
                value: isRateMetric(metric)
                    ? value
                        ? `${convertToThousandSeparator(value)}%`
                        : ''
                    : convertToThousandSeparator(value),
            }
        })

        return { ...acc, ...metrics }
    }, {})

    const submittedFollowerCount = data.tasks.reduce(
        (acc, task) => {
            if (task.submitted_follower) {
                return { ...acc, [task.platform]: convertToThousandSeparator(task.submitted_follower) }
            }
            return acc
        },
        {} as Record<PLATFORM_ENUM, string>,
    )

    return {
        id: aggregateRowId,
        uuid: aggregateRowId,
        total: {
            value: totalCount,
        },
        socialMediaInfo: {
            profiles,
        },
        instagramFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.IG] },
        facebookFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.FB] },
        tiktokFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.TIKTOK] },
        twitterFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.TWITTER] },
        ...taskPerformance,
    }
}

export const getIndividualPerformanceColumnsDef = (
    campaign: CampaignState,
    onFavourite: (payload: UpdateAmbassadorFavoritePayload) => void,
    individualPerformance: GetIndividualPerformanceResponse,
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    const [t] = useNewTranslation()
    const tasks = useSelector(selectTasks)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])

    const engagementTask = useMemo(() => {
        // Return only platforms that are not custom
        const filteredTasks = tasks.filter(task => task.type !== TASK_TYPE_ENUM.CUSTOM)
        return _.uniq(filteredTasks.map(task => task.platform))
    }, [tasks])

    const performanceColumns = getPerformanceColumns(individualPerformance)

    if (!campaign) return []

    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return campaignPlatforms.includes(platform)
    }

    const hasEngagementTask = (platform: PLATFORM_ENUM) => {
        return engagementTask.includes(platform)
    }

    const ambassadorPermission = usePermission(Ambassador.AddRemoveFavourite)

    const cols: GridColDefProps[] = [
        ...(ambassadorPermission
            ? [
                  {
                      field: 'favorite',
                      type: 'string',
                      headerName: t('submission.table.favourite'),
                      width: 60,
                      resizable: true,
                      renderCell: function Highlight(params: GridRenderCellParams): React.ReactElement {
                          if (params.row.id === aggregateRowId) {
                              return <Text scale="bodyS500">{t('submission.campaignReport.totalRow.title')}</Text>
                          }

                          return (
                              <HighlightCell
                                  {...params}
                                  icon="star"
                                  isDisabled={false}
                                  indicatorColor={params.row.favorite ? 'yolkYellow' : undefined}
                                  onClick={() => {
                                      onFavourite({
                                          favorited: params.row.favorite,
                                          partiposter_uuid: params.row.profile.profile.uuid,
                                          brand_uuid: campaign?.brand.uuid,
                                          brand_name: campaign?.brand.name,
                                      })
                                  }}
                              />
                          )
                      },
                      sortable: false,
                  },
              ]
            : []),
        {
            field: 'status',
            type: 'string',
            headerName: t('submission.table.statusLabel'),
            headerClassName: 'align-left-header',
            width: 120,
            resizable: true,
            renderCell: (params: GridRenderCellParams) =>
                params.value ? (
                    <ProfileStatusCell width={120}>
                        <Icon name="circle" size="8px" color={params.value?.color} form="fill" />
                        <Text scale="bodyM500" className="status-text">
                            {params.value?.value}
                        </Text>
                    </ProfileStatusCell>
                ) : null,
            sortable: false,
        },

        {
            field: 'profile',
            headerName: t('submission.table.profile'),
            headerClassName: 'align-left-header',
            width: 160,
            resizable: true,
            sortable: false,
            valueGetter: params => {
                if (params.row.id === aggregateRowId) {
                    return 'total'
                }
                return params.row.profile
            },
            renderCell: (props: GridRenderCellParams) => {
                if (props.row.id === aggregateRowId) {
                    return <Text scale="bodyS500">{`${props.row.total.value} Partiposters`}</Text>
                }
                return <ProfileCell {...props} />
            },
            subFields: [
                {
                    field: 'name',
                    headerName: t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            headerName: t('submission.table.socialMediaInfo'),
            headerClassName: 'align-left-header',
            width: 180,
            resizable: true,
            sortable: false,
            renderCell: (props: GridRenderCellParams): React.ReactElement => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return (
                    <SocialMediaInfoCell
                        {...props}
                        onClick={handleUpdateViewed}
                        hideUsername={props.id === aggregateRowId}
                    />
                )
            },
            subFields: [
                ...(hasPlatform(PLATFORM_ENUM.IG)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        ...(hasPlatform(PLATFORM_ENUM.IG)
            ? [
                  {
                      field: 'instagramFollowerCount',
                      headerName: t('submission.table.instagramFollowerCountLabel'),
                      renderHeader: getInfoTooltipHeaderCell({
                          width: 'auto',
                          title: t('submission.table.followerCountLabel'),
                          description: t(
                              'submission.table.followerCountTooltip',
                              'The number Partiposters entered when submitting engagement result for the campaign',
                          ),
                          textColor: 'primary',
                          iconColor: 'primary',
                          options: { truncateText: true },
                      }),
                      width: 160,
                      resizable: true,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      align: 'right' as GridAlignment,
                      sortable: false,
                      filterable: false,
                  },
                  ...(hasEngagementTask(PLATFORM_ENUM.IG)
                      ? [
                            {
                                field: 'instagramUrl',
                                headerName: `Instagram URL - ${t('submission.table.urlLabel')}`,
                                renderHeader: getStepDataHeaderCell(' - '),
                                headerClassName: 'performance-header',
                                width: 140,
                                resizable: true,
                                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                                sortable: false,
                                filterable: false,
                            },
                        ]
                      : []),
              ]
            : []),
        ...(hasPlatform(PLATFORM_ENUM.FB)
            ? [
                  {
                      field: 'facebookFollowerCount',
                      headerName: t('submission.table.facebookFollowerCountLabel'),
                      renderHeader: getInfoTooltipHeaderCell({
                          width: 'auto',
                          title: t('submission.table.followerCountLabel'),
                          description: t(
                              'submission.table.followerCountTooltip',
                              'The number Partiposters entered when submitting engagement result for the campaign',
                          ),
                          textColor: 'primary',
                          iconColor: 'primary',
                          options: { truncateText: true },
                      }),
                      width: 160,
                      resizable: true,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      align: 'right' as GridAlignment,
                      sortable: false,
                      filterable: false,
                  },
                  ...(hasEngagementTask(PLATFORM_ENUM.FB)
                      ? [
                            {
                                field: 'facebookUrl',
                                headerName: `Facebook URL - ${t('submission.table.urlLabel')}`,
                                renderHeader: getStepDataHeaderCell(' - '),
                                headerClassName: 'performance-header',
                                width: 140,
                                resizable: true,
                                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                                sortable: false,
                                filterable: false,
                            },
                        ]
                      : []),
              ]
            : []),
        ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
            ? [
                  {
                      field: 'tiktokFollowerCount',
                      headerName: t('submission.table.tikTokFollowerCountLabel'),
                      renderHeader: getInfoTooltipHeaderCell({
                          width: 'auto',
                          title: t('submission.table.followerCountLabel'),
                          description: t(
                              'submission.table.followerCountTooltip',
                              'The number Partiposters entered when submitting engagement result for the campaign',
                          ),
                          textColor: 'primary',
                          iconColor: 'primary',
                          options: { truncateText: true },
                      }),
                      width: 160,
                      resizable: true,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      align: 'right' as GridAlignment,
                      sortable: false,
                      filterable: false,
                  },
                  ...(hasEngagementTask(PLATFORM_ENUM.TIKTOK)
                      ? [
                            {
                                field: 'tiktokUrl',
                                headerName: `Tiktok URL - ${t('submission.table.urlLabel')}`,
                                renderHeader: getStepDataHeaderCell(' - '),
                                headerClassName: 'performance-header',
                                width: 140,
                                resizable: true,
                                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                                sortable: false,
                                filterable: false,
                            },
                        ]
                      : []),
              ]
            : []),
        ...(hasPlatform(PLATFORM_ENUM.TWITTER)
            ? [
                  {
                      field: 'twitterFollowerCount',
                      headerName: t('submission.table.twitterFollowersLabel'),
                      renderHeader: getInfoTooltipHeaderCell({
                          width: 'auto',
                          title: t('submission.table.followerCountLabel'),
                          description: t(
                              'submission.table.followerCountTooltip',
                              'The number Partiposters entered when submitting engagement result for the campaign',
                          ),
                          textColor: 'primary',
                          iconColor: 'primary',
                          options: { truncateText: true },
                      }),
                      width: 160,
                      resizable: true,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      align: 'right' as GridAlignment,
                      sortable: false,
                      filterable: false,
                  },
                  ...(hasEngagementTask(PLATFORM_ENUM.TWITTER)
                      ? [
                            {
                                field: 'twitterUrl',
                                headerName: `X URL - ${t('submission.table.urlLabel')}`,
                                renderHeader: getStepDataHeaderCell(' - '),
                                headerClassName: 'performance-header',
                                width: 140,
                                resizable: true,
                                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                                sortable: false,
                                filterable: false,
                            },
                        ]
                      : []),
              ]
            : []),
        ...performanceColumns,
    ]

    return cols
}

export const getIndividualPerformanceRowsDef = (
    individualPerformance: GetIndividualPerformanceResponse,
    individualPerformanceStats: GetIndividualPerformanceStatsResponse,
): RowProps => {
    const results = individualPerformance?.results || []

    const rows = results.map((result, index: number) => {
        const { partiposter_profile, tasks } = result

        const { social_media_accounts: socialMediaAccounts } = partiposter_profile

        const igProfile = socialMediaAccounts?.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )
        const fbProfile = socialMediaAccounts?.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )
        const tiktokProfile = socialMediaAccounts?.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )
        const twitterProfile = socialMediaAccounts?.find(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
        )

        const submittedFollowerCount = tasks.reduce(
            (acc, task) => {
                const isSocialTask = Object.values(PLATFORM_ENUM).includes(task.platform)

                if (isSocialTask) {
                    return {
                        ...acc,
                        [task.platform]: convertToThousandSeparator(task.submitted_follower),
                    }
                }
                return acc
            },
            {} as Record<PLATFORM_ENUM, string>,
        )

        const submittedURLs = tasks.reduce(
            (acc, task) => {
                if (task.submitted_url) {
                    return { ...acc, [task.platform]: task.submitted_url }
                }
                return acc
            },
            {} as Record<PLATFORM_ENUM, string>,
        )

        // Create performance metrics for each task
        const performanceMetrics = tasks.reduce((acc: Record<string, { value: string | number }>, task) => {
            if (task.performance && !_.isEmpty(task.performance)) {
                // For each metric in the performance array
                Object.entries(task.performance).forEach(([metric, value]) => {
                    const fieldName = `${task.uuid}__${metric}`
                    acc[fieldName] = {
                        value: isRateMetric(metric)
                            ? value
                                ? `${convertToThousandSeparator(value)}%`
                                : ''
                            : convertToThousandSeparator(value),
                    }
                })
            }
            return acc
        }, {})

        return {
            id: result.submission_uuid,
            uuid: result.submission_uuid,
            favorite: result.favorite,
            status: SUBMISSION_STATUS[result.submission_status],
            profile: { profile: partiposter_profile },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            instagramFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.IG] },
            facebookFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.FB] },
            tiktokFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.TIKTOK] },
            twitterFollowerCount: { value: submittedFollowerCount[PLATFORM_ENUM.TWITTER] },

            instagramUrl: submittedURLs[PLATFORM_ENUM.IG],
            facebookUrl: submittedURLs[PLATFORM_ENUM.FB],
            tiktokUrl: submittedURLs[PLATFORM_ENUM.TIKTOK],
            twitterUrl: submittedURLs[PLATFORM_ENUM.TWITTER],

            ...performanceMetrics,
        }
    })

    const aggregateData = transformPerformanceStats(individualPerformanceStats, individualPerformance.count)

    return {
        rows,
        aggregateRow: aggregateData,
    }
}

const ProfileStatusCell = styled.div<{ width?: number }>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};
    width: ${({ width }) => width || '0'}px;

    .status-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`
